"use client";

import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { type InputProps } from "@/components/ui/input";

type InputTagsProps = Omit<InputProps, "value" | "onChange"> & {
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
};

const MAX_TAGS = 10;

const InputTags = React.forwardRef<HTMLInputElement, InputTagsProps>(
  ({ className, value, onChange, placeholder, ...props }, ref) => {
    const [pendingDataPoint, setPendingDataPoint] = React.useState("");

    React.useEffect(() => {
      if (pendingDataPoint.includes(",")) {
        const newDataPoints = new Set([
          ...value,
          ...pendingDataPoint
            .split(",")
            .map((chunk) => chunk.trim())
            .filter(Boolean),
        ]);
        if (newDataPoints.size <= MAX_TAGS) {
          onChange(Array.from(newDataPoints));
        } else {
          onChange(Array.from(newDataPoints).slice(0, MAX_TAGS));
        }
        setPendingDataPoint("");
      }
    }, [pendingDataPoint, onChange, value]);

    const addPendingDataPoint = () => {
      if (pendingDataPoint) {
        const newDataPoints = new Set([...value, pendingDataPoint.trim()]);
        if (newDataPoints.size <= MAX_TAGS) {
          onChange(Array.from(newDataPoints));
        } else {
          onChange(Array.from(newDataPoints).slice(0, MAX_TAGS));
        }
        setPendingDataPoint("");
      }
    };

    return (
      <div
        className={cn(
          "flex min-h-10 w-full flex-wrap gap-2 rounded-md border border-input px-3 py-2 text-sm shadow-sm outline-none ring-primary transition transition-colors disabled:cursor-not-allowed disabled:opacity-50 has-[:focus-visible]:ring-1",
          className,
        )}
      >
        {value.map((item) => (
          <Badge key={item} variant="secondary">
            {item}
            <Button
              variant="ghost"
              size="icon"
              className="ml-2 h-3 w-3"
              onClick={() => {
                onChange(value.filter((i) => i !== item));
              }}
            >
              <XIcon className="w-3" />
            </Button>
          </Badge>
        ))}
        {value.length < MAX_TAGS && (
          <input
            className="flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
            value={pendingDataPoint}
            onChange={(e) => setPendingDataPoint(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addPendingDataPoint();
              } else if (
                e.key === "Backspace" &&
                pendingDataPoint.length === 0 &&
                value.length > 0
              ) {
                e.preventDefault();
                onChange(value.slice(0, -1));
              }
            }}
            placeholder={value.length === 0 ? placeholder : ""}
            {...props}
            ref={ref}
          />
        )}
      </div>
    );
  },
);

InputTags.displayName = "InputTags";

export { InputTags };
